import React, { useEffect, useState, useCallback } from "react";
import { Box, CircularProgress, TextField, MenuItem, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { bucketTotalsOf, pendingOf, BUCKET_OF, METH_ORDER, ASSUM_ORANGE, BLOCKED_RED } from "../methodologyUtils";

// Lists project gaps grouped by article / material / supplier
// (GET /projects/:id/gaps?groupBy=article|material|supplier). Each entity is a COMPACT summary
// row (one click opens the ficha) — never expands its gaps inline (that duplicates the ficha).
// Top line = pending work; provenance (asumidos) is secondary metadata; impact distinguishes
// "Bloquea X" (a mandatory M field is unsatisfied) from "X afectado" (only M-A pending).

const useStyles = makeStyles((theme) => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    transition: "border-color .12s, background .12s",
    "&:hover": { borderColor: theme.palette.primary.main, background: theme.palette.action.hover },
  },
  top: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: theme.spacing(2) },
  name: { fontWeight: 700, color: "#4f46e5", fontSize: 15, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  link: { color: theme.palette.primary.main, fontSize: 13, fontWeight: 600, flexShrink: 0 },
  line: { fontSize: 13, marginTop: 3 },
  impact: { fontWeight: 700 },
  meta: { fontSize: 12, color: "#94a3b8", marginTop: 2 },
  domains: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 3 },
}));

const REQ_KEY = { LCA: "requiredForLCA", CEI: "requiredForCEI", EcoScore: "requiredForEcoScore", AGEC: "requiredForAGEC" };
const KIND_LABEL = { article: "Artículo", material: "Materiales", packaging: "Packaging", supplier: "Proveedores" };
const isPendingStatus = (status) => ["faltan", "revisar", "asumibles"].includes(BUCKET_OF[status] || "faltan");

const SORT_OPTIONS = {
  article: [
    { key: "faltan", label: "Más gaps por resolver" },
    { key: "gapCount", label: "Más gaps en total" },
  ],
  material: [
    { key: "faltan", label: "Más gaps por resolver" },
    { key: "dependentArticles", label: "Más artículos afectados" },
  ],
  supplier: [
    { key: "faltan", label: "Más gaps por resolver" },
    { key: "dependentArticles", label: "Más artículos afectados" },
    { key: "dependentMaterials", label: "Más materiales afectados" },
  ],
};

const EntityGapList = ({ api, projectId, groupBy, onOpenEntity, contracted, reloadKey = 0 }) => {
  const classes = useStyles();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("faltan");
  const hasDep = groupBy === "material" || groupBy === "supplier";

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const r = await api("GET", `/projects/${projectId}/gaps?groupBy=${groupBy}`);
    setLoading(false);
    setGroups(r.ok ? r.data.groups ?? [] : []);
    // reloadKey is intentionally a dependency: bumping it re-runs this fetch after a ficha edit.
  }, [api, projectId, groupBy, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSortBy("faltan");
    setSearch("");
  }, [groupBy]);

  const faltanOf = (g) => pendingOf(g.statusCounts);
  const sortVal = (g) => (sortBy === "faltan" ? faltanOf(g) : g[sortBy] ?? 0);
  const displayGroups = groups
    .filter((g) => !search.trim() || (g.entityName || "").toLowerCase().includes(search.trim().toLowerCase()))
    .slice()
    .sort((a, b) => sortVal(b) - sortVal(a));

  const noun = groupBy === "material" ? "materiales" : groupBy === "supplier" ? "proveedores" : "artículos";
  const meths = contracted && contracted.length ? contracted : METH_ORDER;

  // Per-entity: pending per domain + which methodologies are BLOCKED (an unsatisfied M field) vs
  // merely AFFECTED (only M-A pending). We never infer "blocks" just because the methodology has gaps.
  const summarize = (g) => {
    const dom = { article: 0, material: 0, packaging: 0, supplier: 0 };
    const blocks = new Set();
    const affects = new Set();
    (g.gaps || []).forEach((gap) => {
      if (!isPendingStatus(gap.status)) return;
      if (dom[gap.entityKind] != null) dom[gap.entityKind] += 1;
      meths.forEach((m) => {
        const lvl = gap[REQ_KEY[m]];
        if (lvl === "M") {
          blocks.add(m);
          affects.add(m);
        } else if (lvl === "M-A") {
          affects.add(m);
        }
      });
    });
    const blocked = METH_ORDER.filter((m) => blocks.has(m));
    const affectedOnly = METH_ORDER.filter((m) => affects.has(m) && !blocks.has(m));
    const impact = [];
    if (blocked.length) impact.push({ text: `Bloquea ${blocked.join(" · ")}`, color: BLOCKED_RED });
    if (affectedOnly.length)
      impact.push({ text: `${affectedOnly.join(" · ")} afectad${affectedOnly.length === 1 ? "o" : "os"}`, color: ASSUM_ORANGE });
    return { dom, impact };
  };

  return (
    <div>
      <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 12 }}>
        <TextField
          size="small"
          placeholder={`Buscar ${noun}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField select size="small" label="Ordenar por" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ minWidth: 220 }}>
          {(SORT_OPTIONS[groupBy] || SORT_OPTIONS.article).map((o) => (
            <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>
          ))}
        </TextField>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}>
          {displayGroups.length} {noun}
        </span>
      </Box>

      {loading ? (
        <Box textAlign="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : !displayGroups.length ? (
        <Box py={3} textAlign="center" style={{ color: "#94a3b8" }}>
          {search ? `Sin ${noun} que coincidan con "${search}"` : `Sin ${noun} con gaps`}
        </Box>
      ) : (
        displayGroups.map((g) => {
          const b = bucketTotalsOf(g.statusCounts);
          const pendientes = b.faltan + b.revisar + b.asumibles;
          const { dom, impact } = summarize(g);
          return (
            <div
              key={g.entityId}
              className={classes.card}
              onClick={() => onOpenEntity(groupBy, g.entityId, g.entityName, g.gaps)}
            >
              <div className={classes.top}>
                <span className={classes.name}>{g.entityName || g.entityId.slice(0, 8)}</span>
                <span className={classes.link}>Ver detalle →</span>
              </div>
              <div className={classes.line}>
                <strong>{pendientes}</strong> pendiente{pendientes === 1 ? "" : "s"}
                {impact.map((s, i) => (
                  <span key={i} style={{ color: s.color }} className={classes.impact}>
                    {" · "}
                    {s.text}
                  </span>
                ))}
              </div>
              {(b.asumidos > 0 || hasDep) && (
                <div className={classes.meta}>
                  {b.asumidos > 0 && `${b.asumidos} valor${b.asumidos === 1 ? "" : "es"} asumido${b.asumidos === 1 ? "" : "s"}`}
                  {b.asumidos > 0 && hasDep && " · "}
                  {hasDep &&
                    `🔓 ${groupBy === "supplier" ? `${g.dependentMaterials ?? 0} mat · ${g.dependentArticles} art` : `${g.dependentArticles} art`}`}
                </div>
              )}
              {groupBy === "article" && (
                <div className={classes.domains}>
                  Pendientes por área:{" "}
                  {["article", "material", "packaging", "supplier"].map((k, i) => (
                    <span key={k}>
                      {i > 0 && " · "}
                      {KIND_LABEL[k]} <strong>{dom[k]}</strong>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default EntityGapList;
